import { apiFetch } from "./apiFetch";
import { clearTokens, getRefreshToken, setTokens } from "./tokenStorage";

export interface PlanSummary {
	id: string;
	name: string;
	displayName: string;
}

export interface User {
	id: string;
	email: string;
	name?: string;
	role: "user" | "admin" | "superAdmin";
	status: "active" | "inactive" | "suspended";
	emailVerified?: boolean;
	plan?: PlanSummary | null;
	approvalStatus?: "pending" | "approved" | "rejected";
	nsfwConsentGiven?: boolean;
	nsfwConsentGivenAt?: string | null;
	idVerificationStatus?: string;
	idVerificationSubmittedAt?: string | null;
}

export interface AuthTokens {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
	tokenType: "Bearer";
}

interface LoginResponse {
	user: User;
	tokens: AuthTokens;
}

export const loginUser = async (
	email: string,
	password: string,
): Promise<User> => {
	const response = await apiFetch<LoginResponse>("/auth/login", {
		method: "POST",
		body: JSON.stringify({ email, password }),
	});

	// Always store tokens - ensures single token per user is maintained in browser
	if (response.tokens?.accessToken && response.tokens?.refreshToken) {
		setTokens({
			accessToken: response.tokens.accessToken,
			refreshToken: response.tokens.refreshToken, // Required - single token per user
		});
		console.log("[Auth] Tokens stored after login");
	} else {
		console.warn("[Auth] Missing tokens in login response");
	}

	return response.user;
};

export const logoutUser = async () => {
	const refreshToken = getRefreshToken();
	try {
		if (refreshToken) {
			await apiFetch<{ message: string }>("/auth/logout", {
				method: "POST",
				body: JSON.stringify({ refreshToken }),
			});
		}
	} catch (error) {
		// Even if logout fails, clear tokens locally
		console.error("Logout error:", error);
	} finally {
		clearTokens();
	}
};

export const refreshAccessToken = async (): Promise<AuthTokens | null> => {
	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		console.warn("[Auth] No refresh token available");
		return null;
	}

	try {
		const response = await apiFetch<AuthTokens>("/auth/refresh", {
			method: "POST",
			body: JSON.stringify({ refreshToken }),
		});

		// Always store the new refresh token - replaces old one (single token per user)
		if (response?.accessToken && response?.refreshToken) {
			setTokens({
				accessToken: response.accessToken,
				refreshToken: response.refreshToken, // New token replaces old one
			});
			console.log("[Auth] Tokens refreshed and stored");
			return response;
		}

		console.warn("[Auth] Invalid token response format");
		return null;
	} catch (error: any) {
		// Extract error message for better logging
		const errorMessage =
			error?.response?.data?.error?.message ||
			error?.response?.data?.message ||
			error?.message ||
			"Token refresh failed";

		// Log specific error cases
		if (error?.response?.status === 401) {
			console.warn(
				"[Auth] Refresh token invalid or expired (401). User needs to log in again.",
				errorMessage,
			);
		} else if (error?.response?.status === 404) {
			console.error(
				"[Auth] Refresh endpoint not found (404). Check API configuration.",
			);
		} else {
			console.error("[Auth] Token refresh failed:", errorMessage);
		}

		// Refresh failed, clear tokens
		clearTokens();
		throw error;
	}
};

export const checkAuthUser = async (): Promise<User> => {
	return apiFetch<User>("/auth/me");
};

export interface NsfwConsentResponse {
	nsfwConsentGiven: boolean;
	nsfwConsentGivenAt: string;
}

export const giveNsfwConsent = async (): Promise<NsfwConsentResponse> => {
	return apiFetch<NsfwConsentResponse>("/auth/nsfw-consent", {
		method: "POST",
		body: JSON.stringify({ consented: true }),
	});
};

export interface IdVerificationResponse {
	idVerificationStatus: string;
	idVerificationSubmittedAt: string;
	message: string;
}

export const uploadIdVerification = async (
	file: File,
): Promise<IdVerificationResponse> => {
	const formData = new FormData();
	formData.append("file", file);

	return apiFetch<IdVerificationResponse>("/auth/verify-age", {
		method: "POST",
		body: formData,
		// Don't set Content-Type header - browser will set it with boundary
		headers: {},
	});
};
